<script lang="ts">
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { ScripsHelper } from './scripts_helpers';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	//Components
	import ScriptsEditor from './Components/ScriptsEditor.svelte';
	import ScriptListItemV2 from './Components/ScriptListItemV2.svelte';
	//Design system components
	import {
		Alert,
		AlertV2,
		Button,
		ButtonV2,
		Container,
		Icon,
		Loader,
		Modal,
		Stack,
		Text,
	} from '@Shelly/shelly-design-system';
	//Components
	import LibraryScripts from './Components/LibraryScripts.svelte';
	//Types
	import type { Device } from 'src/types/Device';
	import type { Script } from 'src/types/requests/settings/scripts';
	import { onDestroy, getContext } from 'svelte';
	import type { Writable } from 'svelte/store';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { capFirstChar } from 'src/helpers/utilities';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';

	export let device: Device;

	let showLibrary = false,
		editScript: Script = null,
		waitForLoadingListResponse = false,
		waitForDeleteResponse = false,
		deleteScriptPrompt = false,
		scriptId: number | null = null,
		scriptsList: Script[] = [],
		scriptsListError = '',
		showEditScriptModal = false;

	const scripsHelper = new ScripsHelper(device),
		createNewScript = (code: string = ' ') => {
			showEditScriptModal = true;
			editScript = { id: null, name: '', enable: false, running: false, code };
		};

	const deleteScript = async () => {
		if (waitForDeleteResponse) return;
		waitForDeleteResponse = true;
		if (await scripsHelper.deleteScript(scriptId)) {
			deleteScriptPrompt = false;
			editScript = null;
			loadScripts();
		}
		waitForDeleteResponse = false;
	};

	const loadScripts = async () => {
		waitForLoadingListResponse = true;
		scriptsListError = '';
		const { scripts, error } = await scripsHelper.getScripts();
		scriptsList = scripts;
		scriptsListError = error ?? '';
		waitForLoadingListResponse = false;
	};

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	const onCreateScriptSticky = () => createNewScript();

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			stickyBottomAction.set({
				onClick: onCreateScriptSticky,
				icon: 'fas fa-plus',
				loading: waitForLoadingListResponse,
				disabled: waitForLoadingListResponse || scriptsList.length >= 20,
				label: $format('button_create_new_script', { default: 'Create new script' }),
				secondaryButton: {
					onClick: () => (showLibrary = true),
					icon: 'fas fa-book',
					label: $format('button_scripts_open_library', { default: 'Open library' }),
					variant: 'secondary',
					disabled: waitForLoadingListResponse,
				},
				gap: 's',
			});
		} else {
			stickyBottomAction.set(null);
		}
	}

	scripsHelper.addEventListener('create', loadScripts);
	onDestroy(() => {
		scripsHelper.removeEventListener('create', loadScripts);
		stickyBottomAction?.update((current) =>
			current?.onClick === onCreateScriptSticky ? null : (current ?? null),
		);
	});
	loadScripts();
</script>

{#if waitForLoadingListResponse}
	<div class="d-flex justify-content-center">
		<Loader />
	</div>
{:else if scriptsListError}
	<Alert type="danger" heading={scriptsListError} class="mb-s" />
	<ButtonV2
		size="m"
		class="width-full text-capitalized"
		responsiveSize={{
			sm: 'm',
		}}
		variant="primary"
		on:click={loadScripts}
	>
		{$format('button_rescan', { default: 'Rescan' })}
	</ButtonV2>
{:else if !scriptsList?.length || scriptsList[0] == null}
	<AlertV2
		class={classMap({
			'mb-s': scriptsList.length > 0,
		})}
		heading={$format('alert_heading_no_scripts_created', {
			default: 'No scripts created',
		})}
		message={$format('alert_message_create_or_import_scripts', {
			default: 'Use the script functionality to create or import scripts.',
		})}
		isHeadingBold
	/>
{:else}
	<Stack gap="s">
		{#each scriptsList as script, i}
			{#if script?.id}
				<ScriptListItemV2
					scriptID={script.id}
					{scripsHelper}
					on:editScript={() => {
						showEditScriptModal = true;
						editScript = script;
					}}
					on:deleteScript={(e) => {
						scriptId = e.detail.scriptId;
						deleteScriptPrompt = true;
					}}
				/>
			{/if}
		{/each}
	</Stack>
{/if}

<!-- If sticky bottom action is not set, show the buttons used in the case where the scripts UI is a bottom tab button-->
{#if !stickyBottomAction}
	<ButtonV2
		size="m"
		width="full"
		icon={'fas fa-book'}
		class="mt-m"
		variant="secondary"
		on:click={() => (showLibrary = true)}
	>
		{$format('button_scripts_open_library', { default: 'Open library' })}
	</ButtonV2>
	<ButtonV2
		size="m"
		width="full"
		icon={'fas fa-plus'}
		class="mt-s"
		on:click={() => createNewScript()}
		loading={waitForLoadingListResponse}
		disabled={waitForLoadingListResponse || scriptsList.length >= 20}
	>
		{$format('button_create_new_script', { default: 'Create new script' })}
	</ButtonV2>
{/if}

{#if editScript}
	<ScriptsEditor
		bind:showEditScriptModal
		script={editScript}
		{scripsHelper}
		on:close={() => {
			editScript = null;
			showEditScriptModal = false;
		}}
		on:delete={(e) => {
			scriptId = e.detail.scriptId;
			deleteScriptPrompt = true;
		}}
	/>
{/if}
{#if showLibrary}
	<LibraryScripts bind:showModal={showLibrary} on:insertScript={(e) => createNewScript(e.detail)} />
{/if}

{#if deleteScriptPrompt}
	<Modal
		showModal={deleteScriptPrompt}
		backdropClose={false}
		on:close={() => {
			deleteScriptPrompt = false;
		}}
		title={`${$format('delete_script_heading', { default: 'Delete Script' })}?`}
	>
		<Container slot="content" padding="0">
			{@const scriptName = scriptsList.find((el) => el.id === scriptId).name}
			<div class="d-flex flex-direction-column align-items-center">
				<Icon size="xxl" icon="fa-light fa-trash" class="text-danger mb-xs" />
				<Text class="width-full">
					{$format('on_delete_script_msg', {
						values: {
							script_name: scriptName,
						},
						default: `Deleting script '{script_name}' will permanently remove it from your account.`,
					})}
				</Text>
			</div>
		</Container>
		<div class="d-flex justify-content-end" slot="footer">
			<ButtonV2
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				responsiveSize={{
					sm: 'm',
				}}
				variant="secondary"
				on:click={() => (deleteScriptPrompt = false)}
			>
				{$format('button_cancel', { default: 'Cancel' })}
			</ButtonV2>
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				variant="danger"
				loading={waitForDeleteResponse}
				responsiveSize={{
					sm: 'm',
				}}
				on:click={deleteScript}
			>
				{capFirstChar(
					`${$format('delete_script_heading', { default: 'Delete Script' }).toLowerCase()}`,
				)}
			</Button>
		</div>
	</Modal>
{/if}
